import apiService from "../../../../../../service/Api";

export interface MemoryBookApiItem {
  id: number;
  name_of_deceased: string;
  total_comments: number;
  approved_comments: number;
  stream?: {
    id: number;
    title: string;
  };
}

export interface MemoryBooksApiResponse {
  results: MemoryBookApiItem[];
  count?: number;
  next?: string | null;
  previous?: string | null;
}

export interface MinimalUser {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  profile_picture_url: string | null;
}

export interface MemoryBookAssignmentApiItem {
  id: number;
  stream_id: number;
  assigned_to_id: number;
  assigned_to: MinimalUser;
  assigned_by: MinimalUser;
  role?: "scanner" | "uploader" | "both" | "other" | null;
  physical_comments: number | null;
  online_comments: number | null;
  online_remaining_comments: number | null;  
  uploaded_comments: number | null;  
  remaining_comments: string | null;
  due_date: string;
  done_comments: number | null;
  assigned_at: string;
  updated_at: string;
  stream?: {
    id: number;
    title?: string;
    booking?: {
      deceased?: Array<{
        first_name: string;
        surname: string;
        person_image?: string;
      }>;
    };
  };
  memory_book?: {
    is_delivered: boolean;
    physical_comments?: number | null;
    done_comments?: number | null;
    uploaded_comments?: number | null;
    delivery_method?: object;
    delivery_email?: string | null;
    courier_name?: string | null;
    delivered_to?: string | null;
    status?: string;
  } | null;
}

export interface MemoryBookAssignmentsApiResponse {
  results: MemoryBookAssignmentApiItem[];
  count?: number;
  next?: string | null;
  previous?: string | null;
}

export interface UpdateMemoryBook {
  approved_comments?: number;
}

export type MemoryBookAssignmentRole =
  | "scanner"
  | "uploader"
  | "both"
  | "other"
  | null
  | undefined;

// A "scanner" only handles physical comments; an "uploader" only handles
// online ones. Any other role (both/other/unset) covers both flows.
export const roleIncludesPhysical = (role: MemoryBookAssignmentRole) =>
  role !== "uploader";
export const roleIncludesOnline = (role: MemoryBookAssignmentRole) =>
  role !== "scanner";

export const fetchMemoryBooks = () => {
  return apiService.get<MemoryBooksApiResponse>(
    "/api/streams/memory-book/?status=confirmed",
  );
};

export const updateMemoryBook = (id: string, data: UpdateMemoryBook) => {
  return apiService.patch(`/api/streams/memory-book/${id}/`, data);
};

export const fetchAssignedMemoryBooks = (userId: number) => {
  return apiService.get<MemoryBookAssignmentsApiResponse>(
    `/api/streams/memory-book-assignment/?assigned_to=${userId}`,
  );
};

export const fetchAllMemoryBookAssignments = async () => {
  let url = "/api/streams/memory-book-assignment/";
  let allResults: MemoryBookAssignmentApiItem[] = [];

  while (url) {
    const res = await apiService.get<MemoryBookAssignmentsApiResponse>(url);
    allResults = allResults.concat(res.data.results ?? []);
    url = res.data.next
      ? res.data.next.replace(/^https?:\/\/[^/]+/, "") // strip domain, keep relative path
      : "";
  }

  return { data: { results: allResults, count: allResults.length, next: null, previous: null } };
};
export const updateMemoryBookAssignment = (
  assignmentId: number,
  data: { done_comments?: number, uploaded_comments?: number },
) => {
  return apiService.patch(
    `/api/streams/memory-book-assignment/${assignmentId}/`,
    data,
  );
};

export const reassignMemoryBook = (
  assignmentId: number,
  assignedToId: number,
) => {
  return apiService.patch(
    `/api/streams/memory-book-assignment/${assignmentId}/`,
    { assigned_to_id: assignedToId },
  );
};

export interface AwaitingApprovalComment {
  id: number;
  content: string;
  stream_id: number | null;
  booking_id: number | null;
  subject_title: string | null;
  sender_full_name: string;
  sender_email: string | null;
  comment_source: string;
  created_at: string | null;
}

export interface AwaitingApprovalCommentsResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: AwaitingApprovalComment[];
}

export const fetchAwaitingApprovalComments = (page = 1) => {
  return apiService.get<AwaitingApprovalCommentsResponse>(
    `/api/management/dashboard/awaiting-approval-comments/?page=${page}`,
  );
};

export const approveComment = async (commentId: number) => {
  const body = { is_approved: true };
  try {
    return await apiService.patch(`/api/chats/memorial-message/${commentId}/`, body);
  } catch (error) {
    // The first approve can 500 if a post-approval side effect (websocket
    // broadcast/email/whatsapp) fails, even though the approval itself
    // already committed. Retrying is safe: it's idempotent and the retry
    // skips that side-effect path since the record is already approved.
    return await apiService.patch(`/api/chats/memorial-message/${commentId}/`, body);
  }
};

export const spamComment = (commentId: number) => {
  return apiService.patch(`/api/chats/memorial-message/${commentId}/`, {
    is_spam: true,
    is_approved: false,
  });
};

export const deleteComment = (commentId: number) => {
  return apiService.delete(`/api/chats/memorial-message/${commentId}/`);
};
